/**
 * Device Form - Main coordinator using refactored sub-components
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, Button, TextField, TextArea, Flex, Box, Text, IconButton, Separator, Tabs } from '@radix-ui/themes';
import { Cross2Icon, CubeIcon, PlusIcon } from '@radix-ui/react-icons';
import { Device, DeviceTemplate, DeviceType } from '../../types';
import { useDevices } from '../../contexts/DeviceContext';
import { useTemplates } from '../../contexts/TemplateContext';
import { getAvailableParentDevices } from '../../utils/deviceTree';
import { v4 as uuidv4 } from 'uuid';
import TemplateFormFields from '../TemplateFormFields';
import TemplateSelector from './TemplateSelector';
import ConnectionFields from './ConnectionFields';

interface DeviceFormProps {
  device?: Device;
  onClose: () => void;
}

const DeviceForm: React.FC<DeviceFormProps> = ({ device, onClose }) => {
  const { addDevice, updateDevice, devices } = useDevices();
  const { templates } = useTemplates();
  const isEditing = !!device;

  // Tab selection: 'template' or 'manual'
  const [mode, setMode] = useState<'template' | 'manual'>('template');

  // === TEMPLATE MODE STATE ===
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<DeviceType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // === MANUAL MODE STATE ===
  const [manualTemplate, setManualTemplate] = useState<Partial<DeviceTemplate>>({
    name: '',
    type: 'other',
    brand: '',
    model: '',
    description: '',
    powerInput: undefined,
    powerOutputs: [],
    batteryCapacity: undefined,
    usbInputs: [],
    usbOutputs: [],
  });

  // === SHARED STATE ===
  const [customName, setCustomName] = useState(device?.name || '');
  const [connectedTo, setConnectedTo] = useState<string | null>(device?.connectedTo || null);
  const [usbConnectedTo, setUsbConnectedTo] = useState<string | null>(device?.usbConnection?.sourceDeviceId || null);
  const [selectedPowerPort, setSelectedPowerPort] = useState<number | null>(device?.powerConnection?.outputIndex ?? null);
  const [selectedUsbPort, setSelectedUsbPort] = useState<number | null>(device?.usbConnection?.outputIndex ?? null);

  // Get selected template
  const selectedTemplate = useMemo(() => {
    if (!selectedTemplateId) return null;
    return templates.find((t: DeviceTemplate) => t.id === selectedTemplateId) || null;
  }, [selectedTemplateId, templates]);

  // Auto-generate custom name from template
  useEffect(() => {
    if (selectedTemplate && !customName) {
      const baseName = selectedTemplate.brand 
        ? `${selectedTemplate.brand} ${selectedTemplate.name}` 
        : selectedTemplate.name;
      setCustomName(baseName);
    }
  }, [selectedTemplate, customName]);

  // Initialize manual mode from existing device when editing
  useEffect(() => {
    if (device && isEditing) {
      setMode('manual');
      setCustomName(device.name);
      setConnectedTo(device.connectedTo);
      
      setManualTemplate({
        name: device.name || '',
        type: device.type,
        brand: device.brand || '',
        model: device.model || '',
        description: device.description || '',
        powerInput: device.powerInput || (device.powerRequirement > 0 ? {
          voltage: 12,
          maxCurrent: device.powerRequirement / 12
        } : undefined),
        powerOutputs: device.powerOutputs || (device.passthroughPower > 0 ? [{
          voltage: 12,
          maxCurrent: device.passthroughPower / 12,
          portCount: 1
        }] : []),
        maxTotalCurrent: device.maxTotalCurrent,
        batteryCapacity: device.batteryCapacity,
        usbInputs: device.usbInputs || [],
        usbOutputs: device.usbOutputs || [],
      });
    }
  }, [device, isEditing]);

  // Get available devices to connect to
  const availableParentDevices = useMemo(() => {
    return getAvailableParentDevices(device, devices);
  }, [devices, device]);

  // Get selected power source device
  const selectedSourceDevice = useMemo(() => {
    if (!connectedTo) return null;
    return devices.find(d => d.id === connectedTo) || null;
  }, [connectedTo, devices]);

  // Get selected USB source device
  const selectedUsbSourceDevice = useMemo(() => {
    if (!usbConnectedTo) return null;
    return devices.find(d => d.id === usbConnectedTo) || null;
  }, [usbConnectedTo, devices]);

  // Get devices with USB outputs for USB source selection
  const availableUsbSourceDevices = useMemo(() => {
    return devices.filter(d => 
      d.id !== device?.id && 
      d.usbOutputs && 
      d.usbOutputs.length > 0
    );
  }, [device?.id, devices]);

  // Get available power ports from selected device
  const availablePowerPorts = useMemo(() => {
    if (!selectedSourceDevice?.powerOutputs) return [];
    let globalIndex = 0;
    return selectedSourceDevice.powerOutputs.flatMap((output, outputIndex) => 
      Array.from({ length: output.portCount }, (_, portIndex) => ({
        outputIndex,
        portIndex,
        label: output.label || `${output.voltage}V ${output.maxCurrent}A`,
        voltage: output.voltage,
        maxCurrent: output.maxCurrent,
        globalIndex: globalIndex++
      }))
    );
  }, [selectedSourceDevice]);

  // Get available USB ports from selected device
  const availableUsbPorts = useMemo(() => {
    if (!selectedUsbSourceDevice?.usbOutputs) return [];
    let globalIndex = 0;
    return selectedUsbSourceDevice.usbOutputs.flatMap((output, outputIndex) => 
      Array.from({ length: output.count }, (_, portIndex) => ({
        outputIndex,
        portIndex,
        version: output.version,
        powered: output.powered,
        globalIndex: globalIndex++
      }))
    );
  }, [selectedUsbSourceDevice]);

  // Helpers to get output/port indices from global port index
  const getPowerOutputIndex = (globalIndex: number | null): number => {
    if (globalIndex === null || !availablePowerPorts[globalIndex]) return 0;
    return availablePowerPorts[globalIndex].outputIndex;
  };

  const getPowerPortIndex = (globalIndex: number | null): number => {
    if (globalIndex === null || !availablePowerPorts[globalIndex]) return 0;
    return availablePowerPorts[globalIndex].portIndex;
  };

  const getUsbOutputIndex = (globalIndex: number | null): number => {
    if (globalIndex === null || !availableUsbPorts[globalIndex]) return 0;
    return availableUsbPorts[globalIndex].outputIndex;
  };

  const getUsbPortIndex = (globalIndex: number | null): number => {
    if (globalIndex === null || !availableUsbPorts[globalIndex]) return 0;
    return availableUsbPorts[globalIndex].portIndex;
  };

  // Initialize port selections from existing device connections
  useEffect(() => {
    if (device && isEditing && availablePowerPorts.length > 0 && device.powerConnection) {
      const portIndex = availablePowerPorts.findIndex(
        p => p.outputIndex === device.powerConnection?.outputIndex &&
             p.portIndex === device.powerConnection?.portIndex
      );
      if (portIndex >= 0) {
        setSelectedPowerPort(portIndex);
      }
    }
  }, [device, isEditing, availablePowerPorts]);

  useEffect(() => {
    if (device && isEditing && availableUsbPorts.length > 0 && device.usbConnection) {
      const portIndex = availableUsbPorts.findIndex(
        p => p.outputIndex === device.usbConnection?.outputIndex &&
             p.portIndex === device.usbConnection?.portIndex
      );
      if (portIndex >= 0) {
        setSelectedUsbPort(portIndex);
      }
    }
  }, [device, isEditing, availableUsbPorts]);

  const handleTemplateSubmit = () => {
    if (!selectedTemplate) {
      alert('Please select a device template');
      return;
    }

    const powerRequirement = selectedTemplate.powerInput 
      ? (selectedTemplate.powerInput.voltage * selectedTemplate.powerInput.maxCurrent) 
      : 0;

    let passthroughPower = 0;
    if (selectedTemplate.powerOutputs && selectedTemplate.powerOutputs.length > 0) {
      passthroughPower = selectedTemplate.powerOutputs.reduce((sum, output) => 
        sum + (output.voltage * output.maxCurrent), 0
      );
    }

    const deviceData: Device = {
      id: device?.id || uuidv4(),
      name: customName || selectedTemplate.name,
      type: selectedTemplate.type,
      brand: selectedTemplate.brand,
      model: selectedTemplate.model,
      description: selectedTemplate.description,
      powerInput: selectedTemplate.powerInput,
      powerOutputs: selectedTemplate.powerOutputs,
      maxTotalCurrent: selectedTemplate.maxTotalCurrent,
      batteryCapacity: selectedTemplate.batteryCapacity,
      usbInputs: selectedTemplate.usbInputs,
      usbOutputs: selectedTemplate.usbOutputs,
      powerRequirement,
      passthroughPower,
      connectedTo,
      powerConnection: (connectedTo && selectedPowerPort !== null) ? {
        sourceDeviceId: connectedTo,
        outputIndex: getPowerOutputIndex(selectedPowerPort),
        portIndex: getPowerPortIndex(selectedPowerPort)
      } : undefined,
      usbConnection: (usbConnectedTo && selectedUsbPort !== null) ? {
        sourceDeviceId: usbConnectedTo,
        outputIndex: getUsbOutputIndex(selectedUsbPort),
        portIndex: getUsbPortIndex(selectedUsbPort)
      } : undefined,
      usbPorts: []
    };

    if (isEditing) {
      updateDevice(deviceData);
    } else {
      addDevice(deviceData);
    }
    onClose();
  };

  const handleManualSubmit = async () => {
    if (!manualTemplate.name?.trim()) {
      alert('Please enter a device name');
      return;
    }

    const powerRequirement = manualTemplate.powerInput 
      ? (manualTemplate.powerInput.voltage * manualTemplate.powerInput.maxCurrent) 
      : 0;

    let passthroughPower = 0;
    if (manualTemplate.powerOutputs && manualTemplate.powerOutputs.length > 0) {
      passthroughPower = manualTemplate.powerOutputs.reduce((sum, output) => 
        sum + (output.voltage * output.maxCurrent), 0
      );
    }

    // Auto-submit manual entries as pending templates for admin review
    if (!isEditing) {
      try {
        const { templatesAPI } = await import('../../services/api');
        
        await templatesAPI.submit({
          name: manualTemplate.name,
          type: manualTemplate.type || 'other',
          brand: manualTemplate.brand || undefined,
          model: manualTemplate.model || undefined,
          description: manualTemplate.description || undefined,
          powerInput: manualTemplate.powerInput,
          powerOutputs: manualTemplate.powerOutputs,
          batteryCapacity: manualTemplate.batteryCapacity,
          usbInputs: manualTemplate.usbInputs,
          usbOutputs: manualTemplate.usbOutputs,
        });
        
        console.log('✅ Manual device auto-submitted for admin review');
      } catch (error) {
        console.error('Template auto-submission failed (non-critical):', error);
      }
    }

    const deviceData: Device = {
      id: device?.id || uuidv4(),
      name: manualTemplate.name,
      type: manualTemplate.type || 'other',
      brand: manualTemplate.brand,
      model: manualTemplate.model,
      description: manualTemplate.description,
      powerInput: manualTemplate.powerInput,
      powerOutputs: manualTemplate.powerOutputs,
      maxTotalCurrent: manualTemplate.maxTotalCurrent,
      batteryCapacity: manualTemplate.batteryCapacity,
      usbInputs: manualTemplate.usbInputs,
      usbOutputs: manualTemplate.usbOutputs,
      powerRequirement,
      passthroughPower,
      connectedTo,
      powerConnection: (connectedTo && selectedPowerPort !== null) ? {
        sourceDeviceId: connectedTo,
        outputIndex: getPowerOutputIndex(selectedPowerPort),
        portIndex: getPowerPortIndex(selectedPowerPort)
      } : undefined,
      usbConnection: (usbConnectedTo && selectedUsbPort !== null) ? {
        sourceDeviceId: usbConnectedTo,
        outputIndex: getUsbOutputIndex(selectedUsbPort),
        portIndex: getUsbPortIndex(selectedUsbPort)
      } : undefined,
      usbPorts: []
    };

    if (isEditing) {
      updateDevice(deviceData);
    } else {
      addDevice(deviceData);
    }
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (mode === 'template') {
      handleTemplateSubmit();
    } else {
      handleManualSubmit();
    }
  };

  return (
    <Dialog.Root open={true} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content style={{ maxWidth: 700, maxHeight: '90vh', display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
        <Box p="4" style={{ borderBottom: '1px solid var(--gray-6)' }}>
          <Dialog.Title>{isEditing ? 'Edit Device' : 'Add New Device'}</Dialog.Title>
          <Dialog.Description size="2" mt="2">
            {isEditing ? 'Update device configuration' : 'Select from library or create a custom device'}
          </Dialog.Description>
        </Box>
        
        <Box p="4" style={{ flex: 1, overflow: 'auto' }}>
          <form id="device-form" onSubmit={handleSubmit}>
            {!isEditing && (
              <Tabs.Root value={mode} onValueChange={(value) => setMode(value as 'template' | 'manual')}>
                <Tabs.List mb="4">
                  <Tabs.Trigger value="template">
                    <CubeIcon /> From Template
                  </Tabs.Trigger>
                  <Tabs.Trigger value="manual">
                    <PlusIcon /> Manual Entry
                  </Tabs.Trigger>
                </Tabs.List>

                {/* TEMPLATE MODE */}
                <Tabs.Content value="template">
                  <Flex direction="column" gap="4">
                    <TemplateSelector
                      templates={templates}
                      selectedTemplateId={selectedTemplateId}
                      onSelectTemplate={setSelectedTemplateId}
                      filterType={filterType}
                      onFilterTypeChange={setFilterType}
                      searchQuery={searchQuery}
                      onSearchQueryChange={setSearchQuery}
                    />

                    {selectedTemplate && (
                      <>
                        <Separator size="4" />
                        
                        <Box>
                          <Text as="label" size="2" weight="bold" htmlFor="customName">
                            Device Name *
                          </Text>
                          <TextField.Root
                            id="customName"
                            value={customName}
                            onChange={(e) => setCustomName(e.target.value)}
                            placeholder={`${selectedTemplate.brand} ${selectedTemplate.name}`}
                            required
                            mt="1"
                          />
                          <Text as="p" size="1" color="gray" mt="1">
                            Give this device a unique name
                          </Text>
                        </Box>
                      </>
                    )}
                  </Flex>
                </Tabs.Content>

                {/* MANUAL MODE */}
                <Tabs.Content value="manual">
                  <TemplateFormFields
                    template={manualTemplate}
                    onChange={(updated) => setManualTemplate({ ...manualTemplate, ...updated })}
                    showTypeSelector={true}
                  />
                </Tabs.Content>
              </Tabs.Root>
            )}

            {/* EDIT MODE */}
            {isEditing && (
              <>
                <TemplateFormFields
                  template={manualTemplate}
                  onChange={(updated) => setManualTemplate({ ...manualTemplate, ...updated })}
                  showTypeSelector={true}
                />
                <Separator size="4" my="4" />
              </>
            )}

            {/* SHARED FIELDS - Connection and Notes */}
            {(isEditing || selectedTemplate || mode === 'manual') && (
              <>
                {!isEditing && <Separator size="4" my="4" />}
                
                <Flex direction="column" gap="3">
                  <ConnectionFields
                    connectedTo={connectedTo}
                    onConnectedToChange={setConnectedTo}
                    selectedPowerPort={selectedPowerPort}
                    onPowerPortChange={setSelectedPowerPort}
                    availableParentDevices={availableParentDevices}
                    selectedSourceDevice={selectedSourceDevice}
                    availablePowerPorts={availablePowerPorts}
                    usbConnectedTo={usbConnectedTo}
                    onUsbConnectedToChange={setUsbConnectedTo}
                    selectedUsbPort={selectedUsbPort}
                    onUsbPortChange={setSelectedUsbPort}
                    availableUsbSourceDevices={availableUsbSourceDevices}
                    selectedUsbSourceDevice={selectedUsbSourceDevice}
                    availableUsbPorts={availableUsbPorts}
                    devices={devices}
                    editingDeviceId={device?.id}
                  />
                </Flex>
              </>
            )}
          </form>
        </Box>
        
        <Box p="3" style={{ borderTop: '1px solid var(--gray-6)', backgroundColor: 'var(--gray-2)' }}>
          <Flex gap="3" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" type="button">
                Cancel
              </Button>
            </Dialog.Close>
            <Button type="submit" form="device-form" disabled={!isEditing && mode === 'template' && !selectedTemplate}>
              {isEditing ? 'Save Changes' : 'Add Device'}
            </Button>
          </Flex>
        </Box>

        <Dialog.Close>
          <IconButton
            style={{ position: 'absolute', top: '12px', right: '12px' }}
            variant="ghost"
            color="gray"
          >
            <Cross2Icon />
          </IconButton>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default DeviceForm;
